import React, { useState, useRef, useEffect } from 'react';
import styles from './ContactSection.module.css';

const ContactSection = ({ openSection, setOpenSection, sectionKey, inspectionCompleted, selectedStation, selectedCarType, selectedDate, selectedTime, selectedTimeStamp, carBrand, carModel, carPlate, inspectionComment, onClose }) => {
  const [currentMode, setCurrentMode] = useState('collapsed');
  const [values, setValues] = useState({
    name: '',
    phone: '',
    consent: false,
    agreement: false,
  });
  const [price, setPrice] = useState(0);

  useEffect(() => {
    if (openSection !== sectionKey) {
      setCurrentMode('collapsed');
    } else if (openSection === sectionKey && currentMode === 'collapsed') {
      enterForm()
    }
  }, [openSection, sectionKey, currentMode]);

  const bodyRef = useRef(null);

  const animateBody = (targetStyles) => {
    if (!bodyRef.current) return;

    // eslint-disable-next-line no-unused-expressions
    bodyRef.current.offsetHeight; 
    requestAnimationFrame(() => {
      bodyRef.current.style.transition =
        'opacity 0.6s ease-in-out, transform 0.6s ease-in-out';
      Object.assign(bodyRef.current.style, targetStyles);
    });
  };

  const enterCollapsed = () => {
    setCurrentMode('collapsed');
    animateBody({ opacity: '0', transform: 'translateY(-20px)' });
    setTimeout(() => {
      if (bodyRef.current) bodyRef.current.style.display = 'none';
    }, 300);
  };

  const enterForm = () => {
    setCurrentMode('form');
    if (bodyRef.current) {
      bodyRef.current.style.display = 'flex';
      bodyRef.current.style.opacity = '0';
      bodyRef.current.style.transform = 'translateY(-20px)';
      animateBody({ opacity: '1', transform: 'translateY(0)' });
    }
  };

  const handleChevronClick = () => {
    if (currentMode === 'form') {
      setOpenSection(null);
      enterCollapsed();
    } else {
      if (!inspectionCompleted) return; 
      setOpenSection(sectionKey);
      enterForm();
    }
  };

  const isPhoneValid = (phone) => {
    const phoneRegex = /^(\+7|7|8)?[\s\-]?\(?[489][0-9]{2}\)?[\s\-]?[0-9]{3}[\s\-]?[0-9]{2}[\s\-]?[0-9]{2}$/;
    return phoneRegex.test(phone);
  };

  useEffect(() => {
    if (selectedStation?.id && selectedCarType?.id) {
      const fetchPrice = async () => {
        try {
          const url = `${window.apiBaseUrl || 'https://togto.ru/api/'}price.php?stationId=${selectedStation.id}&carTypeId=${selectedCarType.id}`;
          const response = await fetch(url);
          const text = await response.text();
          let data;
          try {
            const parsed = JSON.parse(text);
            if (typeof parsed === 'string') {
              data = JSON.parse(parsed);
            } else {
              data = parsed;
            }
          } catch (e) {
            data = {};
          }
          const newPrice = data['price'] || 0;
          setPrice(newPrice);
        } catch (error) {
          setPrice(0);
        }
      };
      fetchPrice();
    } else {
      setPrice(0);
    }
  }, [selectedStation, selectedCarType]);

  const isCompleted = values.name.trim() && isPhoneValid(values.phone) && values.consent;

  return (
    <div
      className={`${styles.section} ${styles.sectionPrimary}`}
      data-contact-section
    >
      <div className={styles.sectionInner}>
        <div className={styles.sectionHeader} onClick={handleChevronClick}>
          <div className={styles.titles}>
            <div className={styles.icon} data-contact-icon>
              <svg
                width="30"
                height="30"
                viewBox="0 0 30 30"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15 14.2947C16.8233 14.2947 18.5719 13.5417 19.8612 12.2013C21.1505 10.8609 21.8748 9.04294 21.8748 7.14735C21.8748 5.25175 21.1505 3.4338 19.8612 2.09341C18.5719 0.753022 16.8233 0 15 0C13.1767 0 11.4281 0.753022 10.1388 2.09341C8.84953 3.4338 8.12523 5.25175 8.12523 7.14735C8.12523 9.04294 8.84953 10.8609 10.1388 12.2013C11.4281 13.5417 13.1767 14.2947 15 14.2947ZM15 17.4349C5.85955 17.4349 0 22.679 0 25.2323V30H30V25.2323C30 22.1445 24.4527 17.4349 15 17.4349Z"
                  fill={isCompleted ? "#F92C2C" : "#D9D9D9"}
                />
              </svg>
            </div>
            <div className={styles.sectionName}>
              <div className={styles.sectionTitle}>Контакты</div>
              <div className={styles.sectionSubtitle}>
                Для подтверждения записи
              </div>
            </div>
          </div>
          <div
            className={`${styles.chevron} ${
              currentMode === 'form' ? styles.chevronUp : styles.chevronDown
            }`}
          ></div>
        </div>

        <div
          className={styles.sectionBody}
          ref={bodyRef}
          style={{ display: currentMode === 'collapsed' ? 'none' : 'flex' }}
        >
          <div className={styles.form}>
            <div className={styles.inputsRow}>
              <div className={`${styles.fieldInput} ${values.name.trim() ? styles.filled : ''}`}>
                <input
                  className={styles.fieldControl}
                  type="text"
                  placeholder="ФИО"
                  value={values.name}
                  onChange={(e) => setValues(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>
              <div className={`${styles.fieldInput} ${isPhoneValid(values.phone) ? styles.filled : (values.phone && !isPhoneValid(values.phone) ? styles.error : '')}`}>
                <input
                  className={styles.fieldControl}
                  type="tel"
                  placeholder="Телефон"
                  value={values.phone}
                  onChange={(e) => setValues(prev => ({ ...prev, phone: e.target.value }))}
                />
              </div>
            </div>

            <div className={styles.agreement}>
              <input
                type="checkbox"
                id="consent"
                checked={values.consent}
                onChange={(e) => setValues(prev => ({ ...prev, consent: e.target.checked }))}
                className={styles.checkbox}
              />
              <label htmlFor="consent" className={styles.checkboxLabel}>
                Я согласен с обработкой моих персональных данных на сайте оператора
              </label>
            </div>

            <div className={styles.agreement}>
              <input
                type="checkbox"
                id="osago"
                checked={values.agreement}
                onChange={(e) => setValues(prev => ({ ...prev, agreement: e.target.checked }))}
                className={styles.checkbox}
              />
              <label htmlFor="osago" className={styles.checkboxLabel}>
                Мне нужен полис ОСАГО
              </label>
            </div>
          </div>

          <div className={styles.footer}>
            <button
              className={styles.buttonAccent}
              disabled={!isCompleted}
              onClick={async () => {
                const dateStr = selectedTimeStamp;
                // Create FormData with original field names
                const orderData = new FormData();
                orderData.append('stationId', selectedStation?.id || '');
                orderData.append('carTypeId', selectedCarType?.id || '');
                orderData.append('date', dateStr);
                orderData.append('brand', carBrand || '');
                orderData.append('model', carModel || '');
                orderData.append('clientFio', values.name.trim());
                orderData.append('phone', values.phone);
                orderData.append('regNumber', carPlate || '');
                if (values.agreement === true) {
                  orderData.append('osago', values.agreement);
                }
                orderData.append('comment', inspectionComment || '');

                
                try {
                  const response = await fetch(`${window.apiBaseUrl || 'https://togto.ru/api/'}addOrder.php`, {
                    method: 'POST',
                    body: orderData,
                  });
                   console.log(orderData)
                  if (response.ok) {
                    const text = await response.text();
                    const uid = text.trim();
                    if (uid) {
                      window.location.href = `https://togto.ru/order/?uid=${encodeURIComponent(uid)}`;
                    } else {
                      onClose();
                    }
                  } else {
                    alert('Ошибка при создании записи.');
                  }
                } catch (error) {
                  alert('Ошибка при отправке данных.');
                }
              }}
            >
              <span className={styles.buttonLeft}>{`${price} р.`}</span>
              <span className={styles.buttonRight}>Записаться</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactSection;
